import { ArrowRight, Check, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DataSurface } from "@/components/ui/DataSurface";
import { ROUTES } from "@/constants/routes";

const workoutRows = [
  {
    exercise: "Barbell Bench Press",
    sets: 4,
    reps: 6,
    weight: 80,
    rpe: 7.5,
    rest: "2:30",
    isAddedWeight: false,
  },
  {
    exercise: "Chest Supported Row",
    sets: 4,
    reps: 8,
    weight: 60,
    rpe: 8,
    rest: "2:00",
    isAddedWeight: false,
  },
  {
    exercise: "Overhead Press",
    sets: 3,
    reps: 6,
    weight: 50,
    rpe: 8,
    rest: "2:00",
    isAddedWeight: false,
  },
  {
    exercise: "Pull-up",
    sets: 3,
    reps: 8,
    weight: 5,
    rpe: 8,
    rest: "1:45",
    isAddedWeight: true,
  },
] as const;

const metricKeys = ["sets", "reps", "weight", "rpe", "rest"] as const;
type WorkoutRow = (typeof workoutRows)[number];
type MetricKey = (typeof metricKeys)[number];

export function WorkoutLogPreview() {
  const { t, i18n } = useTranslation();
  const numberFormatter = new Intl.NumberFormat(
    i18n.resolvedLanguage || i18n.language,
    { maximumFractionDigits: 1 },
  );
  const formatMetric = (row: WorkoutRow, key: MetricKey) => {
    if (key === "rest") return row.rest;
    if (key === "weight") {
      const prefix = row.isAddedWeight ? "+" : "";
      return `${prefix}${numberFormatter.format(row.weight)} kg`;
    }
    return numberFormatter.format(row[key]);
  };

  return (
    <section
      id="product-proof"
      className="bg-background px-4 py-20 md:px-8 md:py-28"
    >
      <div className="mx-auto max-w-[1280px]">
        <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-sm font-semibold uppercase text-muted">
              {t("frontpage.precision.log.eyebrow")}
            </p>
            <h2 className="mt-2 max-w-[18ch] font-display text-[36px] font-bold leading-[1.05] md:text-[52px]">
              {t("frontpage.precision.log.headline")}
            </h2>
          </div>
          <p className="max-w-[44ch] text-base text-muted">
            {t("frontpage.precision.log.body")}
          </p>
        </div>

        <DataSurface
          title={t("frontpage.precision.log.session")}
          description={t("frontpage.precision.log.duration")}
          action={
            <Link
              to={ROUTES.SIGNUP}
              className="hidden min-h-11 items-center justify-center gap-2 border border-brand bg-brand px-4 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#171918] sm:inline-flex"
            >
              {t("frontpage.precision.log.finish")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          }
          className="border-[#353a35] bg-[#171918] text-white [&>div:first-child]:border-[#353a35] [&>div:last-child]:divide-[#353a35] [&_h2]:text-white [&_p]:text-white/60"
        >
          <Link
            to={ROUTES.SIGNUP}
            className="flex min-h-11 w-full items-center justify-center gap-2 border border-brand bg-brand px-4 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#171918] sm:hidden"
          >
            {t("frontpage.precision.log.finish")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>

          <table className="hidden w-full table-fixed text-left text-sm lg:table">
            <thead className="text-xs uppercase text-white/55">
              <tr>
                <th scope="col" className="w-[34%] px-5 py-3 font-semibold">
                  {t("frontpage.precision.log.exercise")}
                </th>
                {metricKeys.map((key) => (
                  <th scope="col" key={key} className="px-3 py-3 font-semibold">
                    {t(`frontpage.precision.log.${key}`)}
                  </th>
                ))}
                <th scope="col" className="w-12">
                  <span className="sr-only">
                    {t("frontpage.precision.log.completed")}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#353a35]">
              {workoutRows.map((row) => (
                <tr key={row.exercise}>
                  <th scope="row" className="px-5 py-4 font-semibold text-white">
                    {row.exercise}
                  </th>
                  {metricKeys.map((key) => (
                    <td key={key} className="px-3 py-4 tabular-nums text-white/80">
                      {formatMetric(row, key)}
                    </td>
                  ))}
                  <td className="px-3 py-4 text-center">
                    <span className="sr-only">
                      {t("frontpage.precision.log.completed")}
                    </span>
                    <Check
                      className="mx-auto h-4 w-4 text-[hsl(var(--brand-on-dark))]"
                      aria-hidden
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="divide-y divide-[#353a35] lg:hidden">
            {workoutRows.map((row) => (
              <dl key={row.exercise} className="relative grid grid-cols-2 gap-x-6 gap-y-4 px-4 py-5">
                <div className="col-span-2 pr-8">
                  <dt className="text-xs font-semibold uppercase text-white/50">
                    {t("frontpage.precision.log.exercise")}
                  </dt>
                  <dd className="mt-1 font-semibold text-white">{row.exercise}</dd>
                </div>
                {metricKeys.map((key) => (
                  <div key={key}>
                    <dt className="text-xs font-semibold uppercase text-white/50">
                      {t(`frontpage.precision.log.${key}`)}
                    </dt>
                    <dd className="mt-1 tabular-nums text-white/80">
                      {formatMetric(row, key)}
                    </dd>
                  </div>
                ))}
                <div className="absolute right-4 top-5">
                  <dt className="sr-only">
                    {t("frontpage.precision.log.completed")}
                  </dt>
                  <dd>
                    <span className="sr-only">
                      {t("frontpage.precision.log.completed")}
                    </span>
                    <Check
                      className="h-4 w-4 text-[hsl(var(--brand-on-dark))]"
                      aria-hidden
                    />
                  </dd>
                </div>
              </dl>
            ))}
          </div>

          <Link
            to={ROUTES.SIGNUP}
            className="flex min-h-11 w-full items-center gap-2 bg-[#202320] px-4 text-sm font-semibold text-[hsl(var(--brand-on-dark))] transition-colors duration-150 hover:bg-[#292d29] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--brand-on-dark))] md:px-5"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t("frontpage.precision.log.add_exercise")}
          </Link>
        </DataSurface>
      </div>
    </section>
  );
}
